/**
 * The transport bar: what is playing, the controls, the waveform seek bar, and
 * volume -- at the bottom of every layout, as in every music player.
 */

import { useState } from "react";
import { useApp } from "../core/app";
import { player as libraryPlayer, usePlayer, type Player } from "../core/player";
import { Artwork } from "./Artwork";
import { Icon } from "./Icons";
import { MoreButton } from "./PopupMenu";
import { QueuePanel } from "./QueuePanel";
import { MOD } from "./menus";
import { Waveform, clockTime } from "./Waveform";

export function NowPlaying({ size = 48 }: { size?: number }) {
  const p = usePlayer();
  const app = useApp();
  const t = p.current?.track;
  return (
    <div className="nv-np">
      <Artwork trackId={t?.id} seed={t ? t.album || t.artist || t.title : "nova"} size={size} rounded={7} />
      <div className="nv-np-text">
        <b title={t?.title}>{t?.title ?? "Nothing playing"}</b>
        <span title={t ? [t.artist, t.album].filter(Boolean).join(" · ") : undefined}>
          {t ? (
            <>
              <button className="nv-link" onClick={() => { app.setInput(`artist:"${t.artist}"`); app.navigate("songs"); }}>{t.artist || "Unknown artist"}</button>
              {t.bpm != null && <span className="nv-dim"> · {Math.round(t.bpm)} BPM</span>}
              {t.musicalKey && t.musicalKey !== "ambiguous" && <span className="nv-dim"> · {t.musicalKey}</span>}
            </>
          ) : (
            <span className="nv-dim">Double-click a song to play it</span>
          )}
        </span>
      </div>
    </div>
  );
}

export function Controls({ big = false, of: player = libraryPlayer }: { big?: boolean; of?: Player }) {
  const p = usePlayer(player);
  const none = !p.queue.length;
  return (
    <div className={`nv-controls ${big ? "big" : ""}`}>
      <button className={`nv-iconbtn ${p.shuffle ? "on" : ""}`} aria-pressed={p.shuffle} title={`Shuffle (${MOD}+S)`} onClick={() => player.setShuffle(!p.shuffle)}>
        <Icon name="shuffle" />
      </button>
      <button className="nv-iconbtn" disabled={none} title={`Previous (${MOD}+←)`} onClick={() => player.prev()}>
        <Icon name="prev" />
      </button>
      <button className="nv-play" disabled={none} title={p.playing ? "Pause (Space)" : "Play (Space)"} aria-label={p.playing ? "Pause" : "Play"} onClick={() => player.toggle()}>
        <Icon name={p.playing ? "pause" : "play"} size={big ? 18 : 15} />
      </button>
      <button className="nv-iconbtn" disabled={none} title={`Next (${MOD}+→)`} onClick={() => player.next({ fade: p.crossfade > 0 })}>
        <Icon name="next" />
      </button>
      <button
        className={`nv-iconbtn ${p.repeat !== "off" ? "on" : ""}`}
        aria-pressed={p.repeat !== "off"}
        title={`Repeat: ${p.repeat === "off" ? "off" : p.repeat === "all" ? "all" : "one song"}`}
        onClick={() => player.cycleRepeat()}
      >
        <Icon name={p.repeat === "one" ? "repeatOne" : "repeat"} />
      </button>
    </div>
  );
}

export function SeekBar({ height = 34 }: { height?: number }) {
  const p = usePlayer();
  return (
    <div className="nv-seekbar">
      <span className="nv-time">{clockTime(p.position)}</span>
      <Waveform trackId={p.current?.track.id ?? null} position={p.position} duration={p.duration} onSeek={(s) => libraryPlayer.seek(s)} height={height} />
      <span className="nv-time">{clockTime(p.duration)}</span>
    </div>
  );
}

export function Volume({ wide = false, of: player = libraryPlayer }: { wide?: boolean; of?: Player }) {
  const p = usePlayer(player);
  return (
    <div className={`nv-volume ${wide ? "wide" : ""}`}>
      <button className="nv-iconbtn" title={`${p.muted ? "Unmute" : "Mute"} (${MOD}+M)`} onClick={() => player.toggleMute()}>
        <Icon name={p.muted || p.volume === 0 ? "mute" : "vol"} />
      </button>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={p.muted ? 0 : p.volume}
        aria-label="Volume"
        style={{ ["--fill" as string]: `${(p.muted ? 0 : p.volume) * 100}%` }}
        onChange={(e) => player.setVolume(Number(e.target.value))}
      />
    </div>
  );
}

export function Transport() {
  const p = usePlayer();
  const [queueOpen, setQueueOpen] = useState(false);
  return (
    <footer className="nv-transport" aria-label="Player">
      <NowPlaying />
      <div className="nv-transport-mid">
        <Controls />
        <SeekBar />
      </div>
      <div className="nv-transport-right">
        <button className={`nv-iconbtn ${queueOpen ? "on" : ""}`} title="Up next" aria-pressed={queueOpen} onClick={() => setQueueOpen((o) => !o)}>
          <Icon name="queue" />
          {p.queue.length > 0 && <span className="nv-badge">{p.queue.length - Math.max(0, p.index)}</span>}
        </button>
        <Volume />
        <MoreButton
          label="Playback options"
          items={() => [
            { id: "levelling", label: "Volume levelling", checked: p.levelling, run: () => libraryPlayer.setLevelling(!p.levelling) },
            { id: "crossfade", label: "Crossfade", submenu: [0, 3, 6, 9, 12].map((s) => ({ id: `x${s}`, label: s ? `${s} seconds` : "Off (gapless)", checked: p.crossfade === s, run: () => libraryPlayer.setCrossfade(s) })) },
            { id: "clear", group: "b", label: "Clear queue", icon: "trash", disabled: !p.queue.length, run: () => libraryPlayer.clear() },
          ]}
        />
      </div>
      {queueOpen && <QueuePanel onClose={() => setQueueOpen(false)} />}
      {p.error && <div className="nv-transport-error" role="alert">{p.error}</div>}
    </footer>
  );
}
